
import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/context/auth/AuthContext";
import { useThemeConfig } from "@/components/ui/theme-config-provider";
import { useInitialSync } from "@/hooks/useInitialSync";
import { useViewportHeight } from "@/hooks/useViewportHeight";
import { useKeyboardAwareScrolling } from "@/hooks/useKeyboardAwareScrolling";
import { SyncStatusIndicator } from "@/components/ui/SyncStatusIndicator";
import { safeToastSuccess, safeToastError } from "@/components/ui/toastManager";
import { useResetModals } from "@/hooks/useResetModals";
import useErrorLogger from "@/hooks/useErrorLogger";
import logger from "@/utils/logger";
import { validatePassword } from "@/utils/security";
import PageSeo from "@/components/seo/PageSeo";

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register, user, isLoading } = useAuth();
  const { triggerSync, isSyncing } = useInitialSync();
  useResetModals();
  const logError = useErrorLogger();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const { theme } = useThemeConfig();

  const containerRef = useRef<HTMLDivElement>(null);

  useViewportHeight();
  useKeyboardAwareScrolling(containerRef, {
    offsetTop: 100,
    offsetBottom: 160,
    behavior: 'smooth'
  });
  
  // Setup effect to handle initial loading
  useEffect(() => {
    // Give the auth system a moment to initialize
    const timer = setTimeout(() => {
      setPageLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // ✅ NUOVO: Listener per eventi post-registrazione
  useEffect(() => {
    const handleUserRegistered = async (event: CustomEvent) => {
      logger.log('[REGISTER_PAGE] User registered, triggering initial sync');
      try {
        await triggerSync();
      } catch (error) {
        logError('Failed to trigger sync after registration',{additionalInfo:error});
      }
    };

    window.addEventListener('userSignedIn', handleUserRegistered as EventListener);

    return () => {
      window.removeEventListener('userSignedIn', handleUserRegistered as EventListener);
    };
  }, [triggerSync]);

  // Redirect if user is already authenticated
  useEffect(() => {
    if (user && !isLoading) {
      navigate("/", { replace: true });
    }
  }, [user, isLoading, navigate]);
  
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const pwCheck = validatePassword(password);
    if (!pwCheck.isValid) {
      safeToastError(pwCheck.error || 'Password non valida');
      setIsSubmitting(false);
      return;
    }

    try {
      await register(email, password, name);
      
      // ✅ NUOVO: Il sync sarà triggerato automaticamente dall'evento userSignedIn
      safeToastSuccess("Registrazione completata! Controlla la tua email per confermare l'account.");
      navigate("/login");
    } catch (error) {
      logError('Errore durante la registrazione',{additionalInfo:error});
      safeToastError("Errore durante la registrazione. Riprova.");
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (pageLoading || isLoading) {
    return (
      <div className="min-viewport-height flex items-center justify-center" style={{
        backgroundColor: theme.backgroundColor,
        color: theme.textColor
      }}>
        <div className="text-center">
          <h1
            className="text-2xl font-bold mb-4"
            style={{ color: theme.sectionTitleColor }}
          >
            Caricamento...
          </h1>
          {isSyncing && (
            <div className="mt-4">
              <SyncStatusIndicator showDetails />
            </div>
          )}
        </div>
      </div>
    );
  }
  
  return (
    <>
      <PageSeo
        title="Registrati a Ceralia – Spesa Semplice"
        description="Crea il tuo profilo gratuito su Ceralia per organizzare la spesa, condividere le liste e restare sincronizzato con il tuo gruppo."
        canonicalPath="/register"
      />
      <div
        ref={containerRef}
        className="min-viewport-height overflow-y-auto flex flex-col p-6 pb-safe"
        style={{
          backgroundColor: theme.backgroundColor,
          color: theme.textColor
        }}
      >
        <div className="mx-auto w-full max-w-md">
          <div className="text-center mb-8">
            <h1
              className="text-3xl font-bold"
              style={{ color: theme.sectionTitleColor }}
            >
              Ceralia - Spesa Semplice
            </h1>
            <p className="mt-2" style={{ color: theme.sectionTextColor }}>
              Registrati per iniziare a usare l'app
            </p>
          </div>

          {/* ✅ NUOVO: Indicatore sync durante registrazione */}
          {isSyncing && (
            <div
              className="mb-4 p-3 rounded-lg border"
              style={{ backgroundColor: `${theme.primaryColor}20`, borderColor: theme.primaryColor }}
            >
              <SyncStatusIndicator showDetails className="justify-center" />
            </div>
          )}

          <div
            className="p-6"
            style={{
              backgroundColor: theme.cardBackgroundColor,
              borderRadius: theme.cardBorderRadius || theme.borderRadius,
              boxShadow: theme.cardShadow,
              padding: theme.spacingBase
            }}
          >
            <form onSubmit={handleRegister} className="space-y-4">
              <div>
                <Label htmlFor="name">Nome</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Il tuo nome"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1"
                  required
                  disabled={isSubmitting}
                  data-keyboard-scroll
                />
              </div>

              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="tuoemail@esempio.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1"
                  required
                  disabled={isSubmitting}
                  data-keyboard-scroll
                />
              </div>

              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1"
                  required
                  minLength={8}
                  disabled={isSubmitting}
                  data-keyboard-scroll
                />
                <p className="text-xs mt-1" style={{ color: theme.sectionTextColor }}>
                  La password deve avere almeno 8 caratteri e includere una maiuscola, una minuscola,
                  un numero e un simbolo
                </p>
              </div>

              <Button
                type="submit"
                className="w-full"
                style={{
                  backgroundColor: theme.primaryColor,
                  borderRadius: theme.buttonBorderRadius,
                  padding: theme.buttonPadding,
                  color: theme.textPrimaryColor,
                  fontWeight: 'bold',
                  transitionDuration: theme.transitionSpeed
                }}
                disabled={isSubmitting || isSyncing}
              >
                {isSubmitting ? "Registrazione in corso..." : "Registrati"}
              </Button>
            </form>

            <Separator className="my-6" />

            <div className="text-center">
              <p className="text-sm" style={{ color: theme.sectionTextColor }}>
                Hai già un account?{" "}
                <Link to="/login" className="hover:underline" style={{
                  color: theme.primaryColor
                }}>
                  Accedi
                </Link>
              </p>
            </div>
          </div>

          <div className="mt-6 text-center">
            <Link to="/" className="text-sm hover:underline" style={{ color: theme.sectionTextColor }}>
              Torna alla home
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default RegisterPage;
